
import React, { useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { ChartType } from '../types';

interface ChartRendererProps {
    chartType: ChartType;
    xAxisLabel: string;
    yAxisLabel: string;
}

const MOCK_DISTRICTS = ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Erode', 'Vellore'];
const MOCK_TALUKS = ['Alandur', 'Ambattur', 'Guindy', 'Mambalam', 'Mylapore', 'Perambur', 'Purasawalkam', 'Sholinganallur'];
const MOCK_MONTHS = ['2023-01', '2023-02', '2023-03', '2023-04', '2023-05', '2023-06', '2023-07', '2023-08', '2023-09', '2023-10', '2023-11', '2023-12'];
const MOCK_STATUS = ['Approved', 'Rejected', 'Pending'];

const generateMockData = (xAxis: string, yAxis: string): any[] => {
    let labels: string[];
    if (xAxis.toLowerCase().includes('district')) {
        labels = MOCK_DISTRICTS;
    } else if (xAxis.toLowerCase().includes('taluk')) {
        labels = MOCK_TALUKS;
    } else if (xAxis.toLowerCase().includes('month') || xAxis.toLowerCase().includes('date')) {
        labels = MOCK_MONTHS;
    } else if (xAxis.toLowerCase().includes('status')) {
        labels = MOCK_STATUS;
    } else {
        labels = Array.from({ length: 8 }, (_, i) => `Category ${i + 1}`);
    }

    return labels.map(label => ({
        [xAxis]: label,
        [yAxis]: Math.floor(Math.random() * (5000 - 100 + 1) + 100)
    }));
};

const COLORS = ['#06b6d4', '#8b5cf6', '#ec4899', '#f97316', '#22c55e', '#facc15'];

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="p-2 bg-slate-700 border border-slate-600 rounded-md shadow-lg">
                <p className="label text-slate-200">{`${label}`}</p>
                <p className="intro text-cyan-400">{`${payload[0].name} : ${payload[0].value.toLocaleString()}`}</p>
            </div>
        );
    }
    return null;
};

export const ChartRenderer: React.FC<ChartRendererProps> = ({ chartType, xAxisLabel, yAxisLabel }) => {
    const data = useMemo(() => generateMockData(xAxisLabel, yAxisLabel), [xAxisLabel, yAxisLabel]);

    const renderChart = () => {
        switch (chartType) {
            case 'bar':
            case 'column':
                return (
                    <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                        <XAxis dataKey={xAxisLabel} stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                        <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{fill: 'rgba(148, 163, 184, 0.1)'}} />
                        <Legend />
                        <Bar dataKey={yAxisLabel} fill="#06b6d4" radius={[4, 4, 0, 0]} />
                    </BarChart>
                );
            case 'line':
                 return (
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                        <XAxis dataKey={xAxisLabel} stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                        <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{stroke: '#06b6d4', strokeWidth: 1}}/>
                        <Legend />
                        <Line type="monotone" dataKey={yAxisLabel} stroke="#06b6d4" strokeWidth={2} activeDot={{ r: 8 }} dot={{r: 4}} />
                    </LineChart>
                );
            case 'pie':
                return (
                    <PieChart>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey={yAxisLabel}
                            nameKey={xAxisLabel}
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                    </PieChart>
                );
            case 'table':
                return (
                    <div className="overflow-auto h-full text-slate-300">
                        <table className="w-full text-left">
                            <thead className="bg-slate-700">
                                <tr>
                                    <th className="p-2">{xAxisLabel}</th>
                                    <th className="p-2">{yAxisLabel}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, index) => (
                                    <tr key={index} className="border-b border-slate-700 hover:bg-slate-700/50">
                                        <td className="p-2">{row[xAxisLabel]}</td>
                                        <td className="p-2">{row[yAxisLabel].toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            default:
                return <p className="text-center text-slate-400">Chart type '{chartType}' is not supported yet. Displaying as a table.</p>;
        }
    }

    return (
        <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
        </ResponsiveContainer>
    );
};
